import apiClient from "./middleware";

export interface DictionaryAllowlistEntry {
  id: string;
  phrase: string;
  caseSensitive: boolean;
  addedAt: string;
  addedBy: string;
}

export interface DictionaryBlocklistEntry {
  id: string;
  phrase: string;
  substitute: string | null;
  caseSensitive: boolean;
  addedAt: string;
  addedBy: string;
}

export interface MetadataSpellcheckRun {
  runId: string;
  dateCreated: string;
  dateCompleted?: string;
  status: string;
  sourceTable?: string;
  rowCounts?: {
    extracted: number;
    prefilterPassed: number;
    merged: number;
    titleChanged?: number;
    openIssuesWritten?: number;
    resolvedCleanSource?: number;
    resolvedCleanCorrected?: number;
  };
  errorMessage?: string;
  llmMode?: string;
}

export interface MetadataSpellcheckRow {
  run_id: string;
  video_id: string;
  channel_id: string | null;
  channel_title?: string | null;
  channel_language?: string | null;
  video_type?: string | null;
  /** VideoAssetMapping VideoPrivacyStatus (Public | Private | Unlisted). */
  video_privacy_status?: string | null;
  /** VideoAssetMapping upload time (from extract; ISO or BQ string). */
  upload_time?: string | null;
  vod_channel_manager_email?: string | null;
  shorts_channel_manager_email?: string | null;
  /** Reviewer: suggested title accepted (checkbox). */
  suggested_title_confirmation?: boolean | null;
  /** Optional reviewer reason when suggestion is rejected. */
  rejection_reason?: string | null;
  custom_rejection_reason?: string | null;
  original_title: string | null;
  corrected_title: string | null;
  title_changed: boolean | null;
  updated_at?: string | null;
  /** From Vertex worker: spelling, punctuation, spacing, capitalisation */
  corrections?: string[] | null;
  /** Worker-derived issue priority (spelling-first triage). */
  priority_level?: "high" | "secondary" | null;
  /** Reason for worker-derived priority. */
  priority_reason?: "spelling" | "non_spelling_only" | "prohibited_term" | null;
  validation_status?: "ok" | "needs_manual_review" | null;
  validation_reason?:
    | "corrected_title_exceeds_youtube_limit"
    | "prohibited_term_no_substitute"
    | "youtube_title_changed"
    | null;
  corrected_title_length?: number | null;
  custom_corrected_title?: string | null;
  blocklist_no_substitute_hits?: Array<{ phrase: string; caseSensitive: boolean }> | null;
  /** UI-only fields — not from API, encoded into row data so DataTable re-renders on change */
  _titleEditOpen?: boolean;
  _titleEditDraft?: string;
  _savingTitleEdit?: boolean;
  _rejectionReasonDraft?: string;
  _customRejectionReasonDraft?: string;
  is_active_issue?: boolean | null;
  review_status?: "pending" | "accepted" | "rejected" | null;
  rejection_recorded_at?: string | null;
  accepted_recorded_at?: string | null;
  reviewed_by_email?: string | null;
  accepted_applied_at?: string | null;
  youtube_apply_status?:
    | "pending"
    | "processing"
    | "succeeded"
    | "failed"
    | "returned_for_review"
    | null;
  youtube_apply_last_attempt_at?: string | null;
  youtube_apply_attempt_count?: number | null;
  youtube_apply_last_error?: string | null;
  youtube_apply_returned_at?: string | null;
  changed?: boolean | null;
  source_title_hash?: string | null;
}

export const metadataSpellcheckApi = {
  getOpenIssuesMetrics: async (params?: {
    managerEmail?: string;
  }): Promise<{
    totalOpenIssues: number;
    totalAcceptedIssues: number;
    totalRejectedIssues: number;
    totalMyOpenIssues?: number;
  }> => {
    const { data } = await apiClient.get("/metadata-spellcheck/issues/metrics", {
      params: {
        managerEmail: params?.managerEmail || undefined,
      },
    });
    return data;
  },

  getIssueFilterOptions: async (): Promise<{
    languages: { label: string; value: string }[];
    videoTypes: { label: string; value: string }[];
    videoPrivacyStatuses: { label: string; value: string }[];
    channelManagers: { label: string; value: string }[];
    channels: { label: string; value: string }[];
    uploadDates: { label: string; value: string }[];
    correctionTypes: { label: string; value: string }[];
  }> => {
    const { data } = await apiClient.get("/metadata-spellcheck/issues/filter-options");
    return data;
  },

  listOpenIssues: async (params: {
    limit?: number;
    startAfter?: string;
    sortBy?: "video_id" | "updated_at";
    sortOrder?: "asc" | "desc";
    reviewStatuses?: ("pending" | "accepted" | "rejected")[];
    managerEmail?: string;
    managerEmails?: string[];
    languages?: string[];
    videoTypes?: string[];
    videoPrivacyStatuses?: string[];
    channelKeys?: string[];
    correctionTypes?: string[];
    uploadDates?: string[];
    validationStatuses?: string[];
    /** Admin-only audit filter (server ignores it for other roles). */
    youtubeApplyStatuses?: string[];
  }): Promise<{
    rows: MetadataSpellcheckRow[];
    limit: number;
    nextStartAfter?: string;
    totalMatching?: number;
  }> => {
    const join = (values?: string[]) =>
      values && values.length > 0 ? values.join(",") : undefined;
    const { data } = await apiClient.get("/metadata-spellcheck/issues", {
      params: {
        limit: params.limit ?? 50,
        startAfter: params.startAfter || undefined,
        sortBy: params.sortBy || undefined,
        sortOrder: params.sortOrder || undefined,
        reviewStatuses: join(params.reviewStatuses),
        managerEmail: params.managerEmail || undefined,
        managerEmails: join(params.managerEmails),
        languages: join(params.languages),
        videoTypes: join(params.videoTypes),
        videoPrivacyStatuses: join(params.videoPrivacyStatuses),
        channelKeys: join(params.channelKeys),
        correctionTypes: join(params.correctionTypes),
        uploadDates: join(params.uploadDates),
        validationStatuses: join(params.validationStatuses),
        youtubeApplyStatuses: join(params.youtubeApplyStatuses),
      },
    });
    return data;
  },

  patchSuggestedTitleConfirmation: async (
    videoId: string,
    suggested_title_confirmation: boolean,
    rejection_reason?: string | null
  ): Promise<void> => {
    await apiClient.patch(
      `/metadata-spellcheck/issues/${encodeURIComponent(videoId)}/suggested-title-confirmation`,
      { suggested_title_confirmation, rejection_reason: rejection_reason ?? null }
    );
  },

  /** Accept: confirms suggested correction and triggers async YouTube apply task. */
  postAcceptIssue: async (videoId: string): Promise<void> => {
    await apiClient.post(
      `/metadata-spellcheck/issues/${encodeURIComponent(videoId)}/accept`,
      {}
    );
  },

  /** Reject: records reviewer decision and optional custom reason. */
  postRejectIssue: async (
    videoId: string,
    body?: {
      rejection_reason?: string | null;
      custom_rejection_reason?: string | null;
    }
  ): Promise<void> => {
    await apiClient.post(
      `/metadata-spellcheck/issues/${encodeURIComponent(videoId)}/reject`,
      {
        rejection_reason: body?.rejection_reason ?? null,
        custom_rejection_reason: body?.custom_rejection_reason ?? null,
      }
    );
  },

  downloadIssuesCsv: async (
    reviewStatus?: "accepted" | "rejected"
  ): Promise<void> => {
    const response = await apiClient.get("/metadata-spellcheck/issues/export/csv", {
      responseType: "blob",
      params: reviewStatus ? { reviewStatus } : undefined,
    });
    const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = reviewStatus
      ? `metadata_audit_${reviewStatus}_issues.csv`
      : "metadata_audit_open_issues.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  },

  postCustomFix: async (videoId: string, customTitle: string): Promise<void> => {
    await apiClient.post(
      `/metadata-spellcheck/issues/${encodeURIComponent(videoId)}/custom-fix`,
      { custom_title: customTitle }
    );
  },

  downloadOpenIssuesCsv: async (): Promise<void> => {
    await metadataSpellcheckApi.downloadIssuesCsv();
  },

  downloadAcceptedIssuesCsv: async (): Promise<void> => {
    await metadataSpellcheckApi.downloadIssuesCsv("accepted");
  },

  downloadRejectedIssuesCsv: async (): Promise<void> => {
    await metadataSpellcheckApi.downloadIssuesCsv("rejected");
  },

  listRuns: async (limit?: number): Promise<{ runs: MetadataSpellcheckRun[] }> => {
    const { data } = await apiClient.get("/metadata-spellcheck/runs", {
      params: { limit },
    });
    return data;
  },

  createRun: async (body?: {
    runId?: string;
    sourceTable?: string;
  }): Promise<{ runId: string; message: string }> => {
    const { data } = await apiClient.post("/metadata-spellcheck/runs", body || {});
    return data;
  },

  getRun: async (
    runId: string
  ): Promise<{
    run: MetadataSpellcheckRun;
    metrics: {
      totalRows: number;
      titleChanged: number;
    } | null;
  }> => {
    const { data } = await apiClient.get(`/metadata-spellcheck/runs/${runId}`);
    return data;
  },

  getRows: async (
    runId: string,
    params: {
      limit?: number;
      /** Firestore document id cursor: start after this video_id */
      startAfter?: string;
      q?: string;
      onlyChanged?: boolean;
    }
  ): Promise<{
    rows: MetadataSpellcheckRow[];
    total: number;
    limit: number;
    nextStartAfter?: string;
  }> => {
    const { data } = await apiClient.get(
      `/metadata-spellcheck/runs/${runId}/rows`,
      {
        params: {
          limit: params.limit ?? 50,
          startAfter: params.startAfter || undefined,
          q: params.q || undefined,
          onlyChanged: params.onlyChanged ? "1" : undefined,
        },
      }
    );
    return data;
  },

  downloadCsv: async (runId: string, onlyChanged?: boolean): Promise<void> => {
    const response = await apiClient.get(
      `/metadata-spellcheck/runs/${runId}/export/csv`,
      {
        responseType: "blob",
        params: onlyChanged ? { onlyChanged: "1" } : {},
      }
    );
    const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `metadata_audit_title_qc_${runId}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  },

  downloadXlsx: async (
    runId: string,
    maxRows?: number,
    onlyChanged?: boolean
  ): Promise<void> => {
    const response = await apiClient.get(
      `/metadata-spellcheck/runs/${runId}/export/xlsx`,
      {
        responseType: "blob",
        params: {
          ...(maxRows ? { maxRows } : {}),
          ...(onlyChanged ? { onlyChanged: "1" } : {}),
        },
      }
    );
    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `metadata_audit_title_qc_${runId}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  },
};

export const dictionaryApi = {
  getAllowlist: async (): Promise<{ entries: DictionaryAllowlistEntry[] }> => {
    const { data } = await apiClient.get("/metadata-spellcheck/dictionary/allowlist");
    return data;
  },

  addAllowlistEntry: async (params: {
    phrase: string;
    caseSensitive: boolean;
  }): Promise<DictionaryAllowlistEntry> => {
    const { data } = await apiClient.post("/metadata-spellcheck/dictionary/allowlist", params);
    return data;
  },

  updateAllowlistEntry: async (
    id: string,
    params: { caseSensitive: boolean }
  ): Promise<DictionaryAllowlistEntry> => {
    const { data } = await apiClient.patch(
      `/metadata-spellcheck/dictionary/allowlist/${encodeURIComponent(id)}`,
      params
    );
    return data;
  },

  deleteAllowlistEntry: async (id: string): Promise<void> => {
    await apiClient.delete(`/metadata-spellcheck/dictionary/allowlist/${encodeURIComponent(id)}`);
  },

  getBlocklist: async (): Promise<{ entries: DictionaryBlocklistEntry[] }> => {
    const { data } = await apiClient.get("/metadata-spellcheck/dictionary/blocklist");
    return data;
  },

  addBlocklistEntry: async (params: {
    phrase: string;
    substitute: string | null;
    caseSensitive: boolean;
  }): Promise<DictionaryBlocklistEntry> => {
    const { data } = await apiClient.post("/metadata-spellcheck/dictionary/blocklist", params);
    return data;
  },

  updateBlocklistEntry: async (
    id: string,
    params: { substitute: string | null; caseSensitive: boolean }
  ): Promise<DictionaryBlocklistEntry> => {
    const { data } = await apiClient.patch(
      `/metadata-spellcheck/dictionary/blocklist/${encodeURIComponent(id)}`,
      params
    );
    return data;
  },

  deleteBlocklistEntry: async (id: string): Promise<void> => {
    await apiClient.delete(`/metadata-spellcheck/dictionary/blocklist/${encodeURIComponent(id)}`);
  },

  getChannelManagerReconcileStatus: async (): Promise<{
    status: ChannelManagerReconcileStatus | null;
  }> => {
    const { data } = await apiClient.get(
      "/metadata-spellcheck/admin/channel-manager-reconcile"
    );
    return data;
  },

  runChannelManagerReconcile: async (params?: {
    dryRun?: boolean;
    channelId?: string;
  }): Promise<{ status: ChannelManagerReconcileStatus }> => {
    const { data } = await apiClient.post(
      "/metadata-spellcheck/admin/channel-manager-reconcile",
      {
        dryRun: params?.dryRun === true,
        channelId: params?.channelId || undefined,
      },
      { timeout: 180000 }
    );
    return data;
  },
};

export interface ChannelManagerReconcileStatus {
  status: "idle" | "running" | "completed" | "failed";
  dryRun: boolean;
  channelId?: string | null;
  startedAt: string;
  completedAt?: string | null;
  triggeredBy: string;
  scanned: number;
  updated: number;
  unchanged: number;
  missingChannel: number;
  missingChannelId: number;
  skippedEmptyManagers: number;
  channelsInSettings: number;
  durationMs?: number | null;
  error?: string | null;
}
